import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { BarChart3 } from 'lucide-react';

import './surveyDailyChart.css';

// Renders the daily counts returned by /api/surveys/responses/counts/daily as
// a vertical bar chart. The endpoint already pre-fills empty days with 0, so
// we iterate `days` as-is.
//
// `day` is a "YYYY-MM-DD" label in the server timezone — we split it instead
// of passing it through `new Date()` (which would treat it as UTC midnight
// and could shift it a day depending on the viewer's zone).
const SurveyDailyChart = ({ days, total, loading, locale }) => {
	const { t } = useTranslation();

	// Thousands separator matching the viewer's locale — applied to every
	// numeric label the chart renders so the bars read like the total header.
	const nfmt = useMemo(() => new Intl.NumberFormat(locale || undefined), [locale]);

	const rows = useMemo(() => {
		const list = Array.isArray(days) ? days : [];
		const max = list.reduce((m, d) => Math.max(m, d?.count || 0), 0);
		return list.map((d) => {
			const [, mm, dd] = (d?.day || '').split('-');
			const shortLabel = mm && dd ? `${dd}/${mm}` : (d?.day || '');
			return {
				day: d?.day,
				shortLabel,
				count: d?.count || 0,
				pct: max > 0 ? ((d?.count || 0) * 100) / max : 0,
			};
		});
	}, [days]);

	const dense = rows.length > 14;
	const totalLabel = nfmt.format(total || 0);

	return (
		<section className='sl-chart'>
			<header className='sl-chart-header'>
				<div className='sl-chart-title'>
					<BarChart3 size={16} />
					{t('surveys.list.chart.title', { defaultValue: 'Capturas por día' })}
				</div>
				<div className='sl-chart-total'>
					<span className='sl-chart-total-value'>{totalLabel}</span>
					<span className='sl-chart-total-label'>{t('surveys.list.chart.total', { defaultValue: 'total en el periodo' })}</span>
				</div>
			</header>

			{loading
				? <div className='sl-chart-empty'>{t('surveys.list.chart.loading', { defaultValue: 'Cargando…' })}</div>
				: total === 0
					? <div className='sl-chart-empty'>{t('surveys.list.chart.empty', { defaultValue: 'Sin capturas en el periodo.' })}</div>
					: (
						<div className={`sl-chart-body${dense ? ' is-dense' : ''}`}>
							{/* Bars and axis are two separate flex rows so rotated
							    labels can't leak into the bar area — same column
							    widths keep them aligned. */}
							<div className='sl-chart-tracks'>
								{rows.map((r) => (
									<div key={r.day} className='sl-chart-col' title={`${r.day}: ${nfmt.format(r.count)}`}>
										<div className='sl-chart-value'>{r.count > 0 ? nfmt.format(r.count) : ''}</div>
										<div className='sl-chart-track'>
											<div className='sl-chart-fill' style={{ height: `${r.pct}%` }} />
										</div>
									</div>
								))}
							</div>
							<div className='sl-chart-axis'>
								{rows.map((r) => (
									<div key={r.day} className='sl-chart-axis-cell'>
										<span className='sl-chart-label'>{r.shortLabel}</span>
									</div>
								))}
							</div>
						</div>
					)
			}
		</section>
	);
};

SurveyDailyChart.propTypes = {
	days: PropTypes.array,
	total: PropTypes.number,
	loading: PropTypes.bool,
	locale: PropTypes.string,
};

export default SurveyDailyChart;
